import { useMemo, type FormEvent } from "react";

export type CreateWebinarPayload = {
  title: string;
  description?: string;
  type: "live" | "evergreen" | "hybrid";
  timezone: string;
  scheduledAt?: string;
  maxAttendees?: number;
  registrationEnabled: boolean;
};

interface CreateWebinarModalProps {
  isSubmitting: boolean;
  submitError: string | null;
  onClose: () => void;
  onSubmit: (payload: CreateWebinarPayload) => Promise<void>;
}

export function CreateWebinarModal({
  isSubmitting,
  submitError,
  onClose,
  onSubmit,
}: CreateWebinarModalProps) {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const timezoneOptions = useMemo(() => {
    const base = ["UTC", browserTimezone, "Europe/Bucharest", "Europe/London", "America/New_York", "America/Los_Angeles"];
    return Array.from(new Set(base.filter(Boolean)));
  }, [browserTimezone]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const title = String(formData.get("title") ?? "").trim();
    const description = String(formData.get("description") ?? "").trim();
    const type = String(formData.get("type") ?? "live") as CreateWebinarPayload["type"];
    const timezone = String(formData.get("timezone") ?? browserTimezone);
    const localDateTime = String(formData.get("scheduledAt") ?? "");
    const maxAttendees = String(formData.get("maxAttendees") ?? "");
    const registrationEnabled = formData.get("registrationEnabled") === "on";

    await onSubmit({
      title,
      description: description || undefined,
      type,
      timezone,
      scheduledAt: localDateTime ? convertZonedLocalToUtcIso(localDateTime, timezone) : undefined,
      maxAttendees: maxAttendees ? Number(maxAttendees) : undefined,
      registrationEnabled,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Create webinar"
        className="w-full max-w-2xl rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Create Webinar</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Create the webinar record, set the schedule timezone, and decide whether public registration opens immediately.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
            Close
          </button>
        </div>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Title</span>
              <input
                required
                name="title"
                defaultValue=""
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                placeholder="Founder session"
              />
            </label>

            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Type</span>
              <select
                name="type"
                defaultValue="live"
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              >
                <option value="live">Live</option>
                <option value="evergreen">Evergreen</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </label>
          </div>

          <label className="grid gap-2 text-sm">
            <span className="font-medium text-[var(--color-foreground)]">Description</span>
            <textarea
              name="description"
              defaultValue=""
              rows={4}
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              placeholder="What this webinar covers and why it matters."
            />
          </label>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Scheduled date & time</span>
              <input
                type="datetime-local"
                name="scheduledAt"
                defaultValue=""
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Timezone</span>
              <select
                name="timezone"
                defaultValue={browserTimezone}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
              >
                {timezoneOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Max attendees</span>
              <input
                type="number"
                min="1"
                name="maxAttendees"
                defaultValue=""
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                placeholder="500"
              />
            </label>
          </div>

          <label className="flex items-start gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-4 text-sm">
            <input
              type="checkbox"
              name="registrationEnabled"
              defaultChecked
              className="mt-1"
            />
            <span>
              <span className="block font-medium text-[var(--color-foreground)]">Enable registration page</span>
              <span className="block text-[var(--color-muted-foreground)]">
                When enabled, the public registration landing page can accept signups for this webinar.
              </span>
            </span>
          </label>

          {submitError ? (
            <div className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
              {submitError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)]">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create Webinar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function parseLocalDateTime(localDateTime: string): ZonedDateParts {
  const match = localDateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    throw new Error("Invalid local datetime");
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

function getZonedDateParts(date: Date, timeZone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const values: Partial<Record<keyof ZonedDateParts, number>> = {};
  for (const part of formatter.formatToParts(date)) {
    switch (part.type) {
      case "year":
      case "month":
      case "day":
      case "hour":
      case "minute":
      case "second":
        values[part.type] = Number(part.value);
        break;
      default:
        break;
    }
  }

  if (
    values.year === undefined
    || values.month === undefined
    || values.day === undefined
    || values.hour === undefined
    || values.minute === undefined
    || values.second === undefined
  ) {
    throw new Error(`Unable to resolve timezone parts for ${timeZone}`);
  }

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  };
}

function getTimeZoneOffsetMs(utcDate: Date, timeZone: string): number {
  const zoned = getZonedDateParts(utcDate, timeZone);
  const localAsUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
  return localAsUtc - utcDate.getTime();
}

function convertZonedLocalToUtcIso(localDateTime: string, timeZone: string): string {
  const parts = parseLocalDateTime(localDateTime);
  const utcGuess = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second));
  const offsetMs = getTimeZoneOffsetMs(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offsetMs).toISOString();
}
